import { FormattedMessage } from 'react-intl';

import Time from '../components/partials/Time';
import {
    useCategoryUrlGenerator,
    useCollectionUrlGenerator,
    useFormatUrlGenerator,
    useTopicUrlGenerator,
} from './useUrlGenerator';

export function useCollectionSurtitle(collection: Record<string, unknown> | null) {
    const {
        type = null,
        surtitle = null,
        itemsCount: snippetItemsCount = null,
        childrenCount: snippetChildrenCount = null,
        issueNumber: snippetIssueNumber = null,
        categories: snippetCategories = null,
        topic: snippetTopic = null,
        format: snippetFormat = null,
        topics: snippetTopics = null,
        formats: snippetFormats = null,
        metadata,
        seasons = null,
        withCategory = false,
        withSerieFormat = false,
        withFormat = false,
        withChildren = false,
        serieFormat: snippetSerieFormat = null,
    } = collection || {};
    const finalSnippetTopics = snippetTopics || (snippetTopic !== null ? [snippetTopic] : []);
    const finalSnippetFormats = snippetFormats || (snippetFormat !== null ? [snippetFormat] : []);
    const {
        itemsCount = snippetItemsCount,
        childrenCount = snippetChildrenCount,
        issueNumber = snippetIssueNumber,
        serieFormat = snippetSerieFormat,
        categories = snippetCategories,
        topics = finalSnippetTopics,
        formats = finalSnippetFormats,
    } = metadata || {};
    const [category = null] = categories || [];
    const [topic = null] = topics || [];
    const [format = null] = formats || [];
    const { label: categoryLabel = null } = category || {};
    const { label: formatLabel = null } = format || {};
    const { label: topicLabel = null } = topic || {};
    const seasonsItemsCount = (seasons || []).reduce(
        (acc, { itemsCount: seasonItemsCount = 0 }) => acc + seasonItemsCount,
        0,
    );
    const isPodcast = type !== null && type.indexOf('podcast') !== -1;
    const isSerie = (type || '').indexOf('serie') !== -1;
    const hasChildren = childrenCount !== null && childrenCount > 0;
    const finalItemsCount = itemsCount || seasonsItemsCount;
    const hasItems = finalItemsCount !== null && finalItemsCount > 0;
    const seasonsMessage =
        hasChildren && withChildren ? (
            <FormattedMessage
                defaultMessage="{count, plural, one {# saison} other {# saisons}}"
                description="Collection surtitle"
                values={{
                    count: childrenCount,
                }}
            />
        ) : null;
    const episodesMessage = hasItems ? (
        <FormattedMessage
            defaultMessage="{count, plural, one {# épisode} other {# épisodes}}"
            description="Collection surtitle"
            values={{
                count: finalItemsCount,
            }}
        />
    ) : null;

    const itemsMessage = hasItems ? (
        <FormattedMessage
            defaultMessage="{count, plural, one {# contenu} other {# contenus}}"
            description="Collection surtitle"
            values={{
                count: finalItemsCount,
            }}
        />
    ) : null;
    const taxonomyLabel = formatLabel || topicLabel || categoryLabel;
    const hasTaxonomy = withCategory && taxonomyLabel !== null;
    const formatMessage =
        (isSerie && serieFormat !== null
            ? serieFormat
                  .split('|')
                  .map((it) => it.trim())
                  .filter((it) => it !== '1 saison')
                  .reverse()
                  .slice(0, 1)
                  .join(' · ')
            : null) ||
        (isSerie && seasonsMessage !== null && episodesMessage !== null ? (
            <FormattedMessage
                defaultMessage="{seasons} · {episodes}"
                description="Collection surtitle"
                values={{
                    seasons: seasonsMessage,
                    episodes: episodesMessage,
                }}
            />
        ) : null) ||
        (isSerie ? seasonsMessage || episodesMessage : itemsMessage);
    let finalSurtitle = null;
    if (!withFormat && surtitle !== null) {
        finalSurtitle =
            withSerieFormat && formatMessage !== null ? (
                <FormattedMessage
                    defaultMessage="{surtitle} · {format}"
                    description="Collection surtitle"
                    values={{
                        surtitle,
                        format: formatMessage,
                    }}
                />
            ) : (
                surtitle
            );
    } else if (!withFormat && hasTaxonomy) {
        finalSurtitle =
            withSerieFormat && formatMessage !== null ? (
                <FormattedMessage
                    defaultMessage="{taxonomy} · {format}"
                    description="Collection surtitle"
                    values={{
                        taxonomy: taxonomyLabel,
                        format: formatMessage,
                    }}
                />
            ) : (
                taxonomyLabel
            );
    } else if (isPodcast) {
        finalSurtitle =
            withSerieFormat && formatMessage !== null ? (
                <FormattedMessage
                    defaultMessage="Balado · {format}"
                    description="Collection surtitle"
                    values={{
                        format: formatMessage,
                    }}
                />
            ) : (
                <FormattedMessage defaultMessage="Balado" description="Collection surtitle" />
            );
    } else if (isSerie) {
        finalSurtitle =
            withSerieFormat && formatMessage !== null ? (
                <FormattedMessage
                    defaultMessage="Série · {format}"
                    description="Collection surtitle"
                    values={{
                        format: formatMessage,
                    }}
                />
            ) : (
                <FormattedMessage defaultMessage="Série" description="Collection surtitle" />
            );
    } else if (type === 'magazine') {
        finalSurtitle =
            issueNumber !== null ? (
                <FormattedMessage
                    defaultMessage="Magazine #{number}"
                    description="Collection surtitle"
                    values={{
                        number: issueNumber,
                    }}
                />
            ) : (
                <FormattedMessage defaultMessage="Magazine" description="Collection surtitle" />
            );
    } else {
        const collectionLabel = taxonomyLabel || (
            <FormattedMessage defaultMessage="Collection" description="Collection surtitle" />
        );
        finalSurtitle =
            itemsMessage !== null ? (
                <FormattedMessage
                    defaultMessage="{label} · {format}"
                    description="Collection surtitle"
                    values={{
                        label: collectionLabel,
                        format: itemsMessage,
                    }}
                />
            ) : (
                collectionLabel
            );
    }
    return finalSurtitle;
}

export function useDocumentSurtitle(
    document: (Partial<FullDocument> & Partial<DocumentSnippet>) | null,
    opts?: Record<string, unknown>,
) {
    const collectionUrl = useCollectionUrlGenerator();
    const formatUrl = useFormatUrlGenerator();
    const categoryUrl = useCategoryUrlGenerator();
    const topicUrl = useTopicUrlGenerator();
    const {
        type,
        surtitle = null,
        metadata = null,
        collection: snippetCollection = null,
        categories: snippetCategories = null,
        date: snippetDate = null,
        topic: snippetTopic = null,
        format: snippetFormat = null,
        topics: snippetTopics = null,
        formats: snippetFormats = null,
        season: snippetSeason = null,
        episode: snippetEpisode = null,
        micromagNumber: snippetMicromagNumber = null,
    } = document || {};
    const snippetCollections = snippetCollection !== null ? [snippetCollection] : [];
    const finalSnippetTopics = snippetTopics || (snippetTopic !== null ? [snippetTopic] : []);
    const finalSnippetFormats = snippetFormats || (snippetFormat !== null ? [snippetFormat] : []);
    const {
        collections = snippetCollections,
        topics = finalSnippetTopics,
        formats = finalSnippetFormats,
        categories = snippetCategories,
        season = snippetSeason,
        episode = snippetEpisode,
        micromagNumber = snippetMicromagNumber,
        date = snippetDate,
    } = metadata || {};
    const [collection = null] = collections || [];
    const [category = null] = categories || [];
    const [topic = null] = topics || [];
    const [format = null] = formats || [];
    const {
        withCategory = false,
        withoutSerie = false,
        withoutMicromagDate = false,
        withoutCollection = false,
        withoutEpisode = false,
        maxCollectionLength = 30,
    } = opts || {};
    const { serie = null } = collection || {};
    let link = null;
    let label = null;
    if (surtitle !== null) {
        label = surtitle;
    } else if (micromagNumber !== null) {
        label =
            date !== null && !withoutMicromagDate ? (
                <FormattedMessage
                    defaultMessage="Micromag {number} - {date}"
                    description="Document surtitle"
                    values={{
                        number: micromagNumber,
                        date: <Time date={date} month="short" textOnly withoutRelative />,
                    }}
                />
            ) : (
                <FormattedMessage
                    defaultMessage="Micromag {number}"
                    description="Document surtitle"
                    values={{
                        number: micromagNumber,
                    }}
                />
            );
    } else if (
        collection !== null &&
        (season !== null || episode !== null || serie !== null) &&
        !withoutEpisode
    ) {
        const serie = collection.serie || collection;
        const isNumeric =
            (season !== null && episode !== null) ||
            (episode !== null && episode.match(/\d+/) !== null);
        const episodeLabel =
            season !== null && episode !== null ? (
                <FormattedMessage
                    defaultMessage="Saison {season} - Ép. {episode}"
                    description="Document surtitle"
                    values={{ season, episode }}
                />
            ) : (
                episode
            );
        const seasonLabel = serie !== null && collection !== null ? collection.title : null;
        if (withoutSerie) {
            label = episodeLabel;
        } else if (isNumeric) {
            label = (
                <>
                    {serie.title}
                    <br />
                    <small>{episodeLabel}</small>
                </>
            );
        } else if (seasonLabel) {
            label = (
                <>
                    {serie.title}
                    <br />
                    <small>{seasonLabel}</small>
                </>
            );
        } else {
            label = (
                <>
                    {serie.title}
                    {' · '}
                    {episodeLabel}
                </>
            );
        }
        link = collectionUrl(serie);
    } else if (
        collection !== null &&
        (maxCollectionLength === null || collection.title.length < maxCollectionLength) &&
        !withoutCollection
    ) {
        label = serie !== null ? serie.title : collection.title;
        link = collectionUrl(serie || collection);
    } else if (topic !== null) {
        label = topic.label;
        link = topicUrl(topic);
    } else if (format !== null) {
        label = format.label;
        link = formatUrl(format);
    } else if (withCategory && category !== null) {
        label = category.label;
        link = categoryUrl(category);
    } else if (type === 'newsletter') {
        label =
            date !== null ? (
                <FormattedMessage
                    defaultMessage="Infolettre du {date}"
                    description="Document surtitle"
                    values={{
                        date: (
                            <Time
                                date={date}
                                month="short"
                                weekday="short"
                                withoutRelative
                                textOnly
                            />
                        ),
                    }}
                />
            ) : (
                <FormattedMessage defaultMessage="Infolettre" description="Document surtitle" />
            );
    }

    if (label === null) {
        return null;
    }

    return {
        url: link,
        label,
    };
}
